/*
 * One value of the tab that several React trees follow, and the renders that follow it.
 *
 * The client has facts that are neither the model's (`store.ts`, which the server's events write)
 * nor one component's: whether a dialog was asked for by a key binding, which row of a list holds
 * the roving stop, the paragraph under a document's caret, the emoji this person used last. Their
 * writers and readers stand in different trees — a shortcut and the dialog it opens, a list and
 * three hundred rows — so they meet in a module-level value. `useSyncExternalStore` needs a
 * snapshot that keeps its identity until the value moves and a `subscribe` that keeps its identity
 * for the life of the value; `Shared` is exactly that, so a store is built on it rather than beside
 * it, and there is one way in the client to keep such a value and to be told it moved.
 *
 * A reader is told only when `same` says the value really moved: a caret that stays in its
 * paragraph, a count that did not change, tell nobody. A store whose readers must hear a call that
 * leaves its snapshot alone — the drop target's stack, the editor's transactions — is not one of
 * these, and keeps its own listeners.
 */

import { useSyncExternalStore } from "react"

export class Shared<T> {
	private value: T
	private readonly same: (a: T, b: T) => boolean
	private readonly listeners = new Set<() => void>()

	constructor(initial: T, same: (a: T, b: T) => boolean = Object.is) {
		this.value = initial
		this.same = same
	}

	/** The value now; the same object until it moves. */
	now = (): T => this.value

	/** Replaces the value, and tells every reader when it moved. */
	set = (next: T): void => {
		if (this.same(this.value, next)) return
		this.value = next
		for (const listener of this.listeners) listener()
	}

	/** Hears every move; the function it returns stops listening. Its identity never changes. */
	subscribe = (listener: () => void): (() => void) => {
		this.listeners.add(listener)
		return () => {
			this.listeners.delete(listener)
		}
	}
}

/** The value of a `Shared`, for a render: it re-renders when the value moves and at no other time. */
export function useShared<T>(shared: Shared<T>): T {
	return useSyncExternalStore(shared.subscribe, shared.now, shared.now)
}
